import React, { Fragment, useMemo, useState } from 'react';
import { ButtonProps, IconButton, Link, MenuItem, styled } from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridCsvExportOptions,
    GridCsvGetRowsToExportParams,
    GridExportMenuItemProps,
    GridRowId,
    GridRowSelectionModel,
    GridToolbarContainer,
    GridToolbarContainerProps,
    GridToolbarExportContainer,
    GridToolbarFilterButton,
    gridExpandedSortedRowIdsSelector,
    useGridApiContext,
    useGridApiRef,
    useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import Icon from '@mdi/react';
import { mdiFinance } from '@mdi/js';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { updateSelectedRecommendations } from '../../state/features/networkRxSlice';
import { StartServiceRecommendation } from '../../interfaces/services/networkRecommendations';
import { ApiStatus } from '../../utils/enums';
import { getEngageLaneLink, getMarketInsightsLink } from '../../utils/navigationHelpers';

import { ReactComponent as EngageLaneBlue } from '../../assets/images/engage-lane-blue.svg';

const classesPrefix = 'startServiceTable';

const classes = {
    table: `${classesPrefix}-table`,
    engageLaneLogo: `${classesPrefix}-engageLaneLogo`,
};

const StyledDataGridPremium = styled(DataGridPremium)(({ theme }) => {
    return {
        [`&.${classes.table}`]: {
            '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                outline: 0,
            },
            '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                outline: 0,
            },
            '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                outline: 0,
            },
            '&.MuiDataGrid-root .MuiDataGrid-toolbarContainer': {
                justifyContent: 'right',
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
        },
        [`& .${classes.engageLaneLogo}`]: {
            fill: theme.palette.primary.main,
        },
    };
}) as typeof DataGridPremium;

const getFilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams): GridRowId[] => {
    return gridExpandedSortedRowIdsSelector(apiRef);
};

const ExportCsvMenuItem = ({
    options,
    hideMenu,
}: GridExportMenuItemProps<{
    label: string;
    csvOptions: GridCsvExportOptions;
}>): React.JSX.Element => {
    const apiRef = useGridApiContext();

    const handleExport = (): void => {
        apiRef.current.exportDataAsCsv({
            ...options?.csvOptions,
            fileName: 'Network Insights - Start Service Recommendations',
        });
    };

    return (
        <MenuItem
            onClick={(): void => {
                handleExport();

                // Hide the export menu after the export
                hideMenu?.();
            }}
        >
            {options?.label}
        </MenuItem>
    );
};

const CustomExportButton = (props: ButtonProps): React.JSX.Element => {
    return (
        <GridToolbarExportContainer {...props}>
            <ExportCsvMenuItem options={{ label: 'CSV', csvOptions: { allColumns: true, getRowsToExport: getFilteredRows } }} />
        </GridToolbarExportContainer>
    );
};

const CustomToolbar = (props: GridToolbarContainerProps): React.JSX.Element => {
    return (
        <GridToolbarContainer {...props}>
            <GridToolbarFilterButton />
            <CustomExportButton />
        </GridToolbarContainer>
    );
};

const StartServiceTable = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const apiRef = useGridApiRef();

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

    const networkRecommendationsApiStatus = useAppSelector((state) => { return state.networkRx.networkRecommendationsApiStatus; });
    const networkRecommendations = useAppSelector((state) => { return state.networkRx.networkRecommendations; });

    const columns = useMemo((): GridColDef<StartServiceRecommendation>[] => {
        return [
            {
                headerName: 'Bundle',
                field: 'bundle_index',
                valueGetter: (_value, row, _column, rowApiRef): string => {
                    const rowId = rowApiRef.current.getRowId(row);
                    const rowNode = rowApiRef.current.getRowNode(rowId);
                    if (rowNode?.type !== 'leaf') {
                        return '';
                    }
                    return `Bundle ${row.bundle_index}`;
                },
                groupingValueGetter: (value): string => {
                    return `Bundle ${value}`;
                },
            } as GridColDef<StartServiceRecommendation, string>,
            {
                headerName: 'Lane',
                field: 'lane_index',
                valueGetter: (_value, row, _column, rowApiRef): string => {
                    const rowId = rowApiRef.current.getRowId(row);
                    const rowNode = rowApiRef.current.getRowNode(rowId);
                    if (rowNode?.type !== 'leaf') {
                        return '';
                    }
                    return `Lane ${row.lane_index}`;
                },
                groupingValueGetter: (value): string => {
                    return `Lane ${value}`;
                },
                groupable: false,
            },
            {
                headerName: 'Shipper',
                field: 'target_pickup_party_name',
                flex: 1,
                groupable: false,
            },
            {
                headerName: 'Origin',
                field: 'pickup_service_area_name',
                flex: 1,
                groupable: false,
            },
            {
                headerName: 'Destination',
                field: 'delivery_service_area_name',
                flex: 1,
                groupable: false,
            },
            {
                headerName: 'Learn More',
                field: 'lane',
                groupable: false,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                align: 'center',
                valueGetter: (): string => {
                    return `Engage Lane: ${getEngageLaneLink()} Market Insights: ${getMarketInsightsLink()}`;
                },
                renderCell: (): React.JSX.Element => {
                    return (
                        <Fragment>
                            <IconButton
                                color='primary'
                                component={Link}
                                underline='none'
                                target='_blank'
                                rel='noopener noreferrer'
                                href={getMarketInsightsLink()}
                            >
                                <Icon path={mdiFinance} size={1} />
                            </IconButton>
                            <IconButton
                                color='primary'
                                component={Link}
                                underline='none'
                                target='_blank'
                                rel='noopener noreferrer'
                                href={getEngageLaneLink()}
                            >
                                <EngageLaneBlue
                                    className={classes.engageLaneLogo}
                                    width='24px'
                                    height='24px'
                                />
                            </IconButton>
                        </Fragment>
                    );
                },
            },
        ];
    }, []);

    const handleRowSelectionChange = (newRowSelectionModel: GridRowSelectionModel): void => {
        // if the existing selected id matches the new one, clear out the selection, otherwise use what MUI passes
        const newModel = newRowSelectionModel.length > 0 && rowSelectionModel.length > 0 &&
            newRowSelectionModel[0] === rowSelectionModel[0] ?
            [] :
            newRowSelectionModel;
        setRowSelectionModel(newModel);

        // if a row is selected, find the full lane details and save to the store, otherwise clear the selected recommendation
        if (newModel.length > 0) {
            // If it's a grouped row being selected it will start with 'auto-generated'. Split off the bundle index to find the corresponding lanes in the bundle.
            if (newModel[0].toString().startsWith('auto-generated')) {
                const [, bundleIndex] = newModel[0].toString().split('Bundle ');
                const selectedRows = networkRecommendations.recommendations.start_service.filter((recommendation) => {
                    return recommendation.bundle_index.toString() === bundleIndex;
                });

                dispatch(updateSelectedRecommendations(selectedRows));
            } else {
                // If it's a single row selected, the string passed will be the id of the row `{recommendation_id}_{lane_index}`. Find the corresponding lane.
                const [recommendationId, laneIndex] = newModel[0].toString().split('_');
                const selectedRow = networkRecommendations.recommendations.start_service.find((recommendation) => {
                    return recommendation.recommendation_id === recommendationId &&
                        recommendation.lane_index.toString() === laneIndex;
                });

                dispatch(updateSelectedRecommendations(selectedRow ? [selectedRow] : []));
            }
        } else {
            dispatch(updateSelectedRecommendations([]));
        }
    };

    const columnSearchPredicate = (column: GridColDef, searchValue: string): boolean => {
        return (
            (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1 ||
            (column.description || '').toLowerCase().indexOf(searchValue) > -1
        );
    };

    const initialState = useKeepGroupedColumnsHidden({
        apiRef,
        initialState: {
            sorting: {
                sortModel: [
                    { field: '__row_group_by_columns_group__', sort: 'asc' },
                    { field: 'lane_index', sort: 'asc' },
                ],
            },
            rowGrouping: {
                model: ['bundle_index'],
            },
        },
    });

    return (
        <StyledDataGridPremium
            apiRef={apiRef}
            classes={{
                root: classes.table,
            }}
            rows={networkRecommendations.recommendations.start_service}
            columns={columns}
            density='compact'
            disableMultipleRowSelection
            disableAggregation
            rowGroupingColumnMode='single'
            defaultGroupingExpansionDepth={-1}
            loading={networkRecommendationsApiStatus === ApiStatus.Idle || networkRecommendationsApiStatus === ApiStatus.Loading}
            getRowId={(row): string => { return `${row.recommendation_id}_${row.lane_index}`; }}
            // rowCount={networkRecommendations.recommendation_count_details.start_service}
            pagination
            pageSizeOptions={[5, 10, 25, 50, 100]}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionChange}
            initialState={initialState}
            groupingColDef={{
                hideDescendantCount: true,
            }}
            slots={{
                toolbar: CustomToolbar,
            }}
            slotProps={{
                columnsManagement: {
                    searchPredicate: columnSearchPredicate,
                },
            }}
        />
    );
};

export default StartServiceTable;
